import React, { useEffect, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import GenericButton, { GenericButtonTypeEnum } from '../GenericButton';
import SegmentedControl from '../SegmentedControl';
import { DEFAULT_CURRENCY, formatMoney, parseMoneyInput, toMoneyInputValue } from '../../utilities/helper/money.helper';

/** Item | Price | Quantity | Total | menu - the same grid for a line and for a discount under it. */
export const LINE_COLUMNS = 'minmax(0, 1fr) 168px 92px 104px 40px';

/** The 44px field of a line; outline and focus come from the theme. */
export const lineFieldSx = {
  '& .MuiOutlinedInput-root': { height: 44, borderRadius: '10px', bgcolor: 'surface.main' },
  '& .MuiOutlinedInput-input': { fontSize: 15, px: '12px' }
} as const;

export const LineTableHeader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      aria-hidden
      sx={{
        display: 'grid',
        gridTemplateColumns: LINE_COLUMNS,
        columnGap: 1.5,
        p: '8px 16px',
        bgcolor: 'surface.subtle',
        borderBottom: 1,
        borderColor: 'line',
        fontSize: 12,
        color: 'text.secondary'
      }}
    >
      <span>{t('billing.lines.columns.item')}</span>
      <span style={{ textAlign: 'right' }}>{t('billing.lines.columns.price')}</span>
      <span style={{ textAlign: 'right' }}>{t('billing.lines.columns.quantity')}</span>
      <span style={{ textAlign: 'right' }}>{t('billing.lines.columns.total')}</span>
      <span />
    </Box>
  );
};

/**
 * A price that shows the real number, not a placeholder.
 *
 * Holds the typed text and parses it on blur, so the caret does not jump when a thousands
 * separator appears. Left empty, it goes back to `fallback` (the catalogue's price).
 */
export const PriceField: React.FC<{
  value: number;
  onChange: (value: number) => void;
  /** What empty means; a line without a catalogue price has none, and empty is zero. */
  fallback?: number;
  ariaLabel: string;
  describedBy?: string;
  strong?: boolean;
  inputRef?: (element: HTMLInputElement | null) => void;
}> = ({ value, onChange, fallback, ariaLabel, describedBy, strong, inputRef }) => {
  const [text, setText] = useState(formatMoney(value));
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!focused) setText(formatMoney(value));
  }, [value, focused]);

  return (
    <TextField
      fullWidth
      value={text}
      error={invalid}
      inputRef={inputRef}
      onFocus={() => {
        setFocused(true);
        setText(toMoneyInputValue(value));
      }}
      onChange={event => {
        if (/^[\d.,\s]*$/.test(event.target.value)) setText(event.target.value);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseMoneyInput(text);
        if (parsed === undefined) {
          setInvalid(true);
          return;
        }
        setInvalid(false);
        const next = parsed === null ? (fallback ?? 0) : parsed;
        setText(formatMoney(next));
        onChange(next);
      }}
      slotProps={{
        htmlInput: {
          inputMode: 'decimal',
          'aria-label': ariaLabel,
          'aria-describedby': describedBy,
          'aria-invalid': invalid,
          style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 600 : 400 }
        },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{DEFAULT_CURRENCY}</Typography>
            </InputAdornment>
          )
        }
      }}
      sx={lineFieldSx}
    />
  );
};

/** "vrati", inline in a hint line. */
export const InlineAction: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <ButtonBase
    onClick={onClick}
    sx={{ fontSize: 12, color: 'primary.main', verticalAlign: 'baseline', '&:hover': { textDecoration: 'underline' } }}
  >
    {children}
  </ButtonBase>
);

/** The small dialog behind "Name on invoice" in a line's menu. */
export const LabelDialog: React.FC<{
  title: string;
  label: string;
  initial: string;
  placeholder?: string;
  hint?: string;
  children?: React.ReactNode;
  onClose: () => void;
  onSave: (value: string) => void;
}> = ({ title, label, initial, placeholder, hint, children, onClose, onSave }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  return (
    <Dialog open onClose={onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 440, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography component="label" htmlFor="line-label-dialog" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
          {label}
        </Typography>
        <TextField id="line-label-dialog" fullWidth autoFocus value={value} placeholder={placeholder} onChange={event => setValue(event.target.value)} sx={lineFieldSx} />
        {hint && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{hint}</Typography>}
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} />
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          label={t('common.save')}
          onClick={() => {
            onSave(value);
            onClose();
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export const VAT_RATES = [20, 10, 0];

/** The small dialog behind "VAT rate" in a line's menu. */
export const VatDialog: React.FC<{
  title: string;
  initial: number;
  hint?: string;
  onClose: () => void;
  onSave: (rate: number) => void;
}> = ({ title, initial, hint, onClose, onSave }) => {
  const { t } = useTranslation();
  const [rate, setRate] = useState(initial);
  return (
    <Dialog open onClose={onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 400, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <SegmentedControl
          ariaLabel={t('billing.lines.vat')}
          value={rate}
          onChange={setRate}
          options={VAT_RATES.map(value => ({ value, label: value === 0 ? t('billing.items.noVat') : `${value} %` }))}
        />
        {hint && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{hint}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} />
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          label={t('common.save')}
          onClick={() => {
            onSave(rate);
            onClose();
          }}
        />
      </DialogActions>
    </Dialog>
  );
};
